import Accordion from '@/common/components/atom/accordion';
import Breadcrumbs from '@/common/components/atom/breadcrumbs';
import { MenuItem, MenuList } from '@/common/components/atom/menu';
import Text from '@/common/components/atom/text';
import ChevronIcon from '@/common/components/icons/chevron';
import useCustomize from '@/common/hooks/useCustomize';
import { useSearch } from '../../hooks/useSearch';
import { Fragment } from '@/common/fragment';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import useIsNewAppShell from '@/common/hooks/useIsNewAppShell';
import { useRouter } from 'next/router';
import { areaRelatedLinks, hasUserQuery } from '../../functions/areaPages';

export const SearchSeoBox = () => {
  const customize = useCustomize(state => state.customize);
  const { seoInfo, footers } = useSearch();
  const showPlasmicSeo = useFeatureIsOn('search_plasmic_seo');
  const isApp = useIsNewAppShell();
  const { asPath } = useRouter();
  const { area, areaRelated } = useSearch();

  // DR-43: the neighbouring neighbourhoods / other specialties. Not a visible chip row (owner, 2026-10-30): a closed
  // <details> under the SEO box, plain <a href> links. NOT handed to the Plasmic box as `footer` groups: Plasmic renders its
  // footer items as clickable <div>s (router.push), so after hydration the links were gone for Google (review 2026-10-31).
  // Only on the clean, indexable area page.
  const relatedGroups = (() => {
    if (!area || hasUserQuery(asPath) || !customize.showSeoBoxs) return [];
    const related = areaRelatedLinks(areaRelated);
    return [
      { title: 'محله‌های هم‌جوار', links: related.nearby },
      { title: `تخصص‌های دیگر در ${area.name_fa}`, links: related.specialties },
    ].filter(g => g.links.length > 0);
  })();
  const relatedBlock = relatedGroups.length > 0 && (
    <details data-testid="area-related-links" className="mt-3 text-sm pwa:hidden text-slate-600">
      <summary className="py-2 cursor-pointer select-none">محله‌ها و تخصص‌های دیگر</summary>
      {relatedGroups.map(group => (
        <nav key={group.title} aria-label={group.title} className="mt-2">
          <h2 className="text-sm font-bold">{group.title}</h2>
          <ul className="mt-1 space-y-1">
            {group.links.map(link => (
              <li key={link.href}>
                <a href={link.href}>{link.title}</a>
              </li>
            ))}
          </ul>
        </nav>
      ))}
    </details>
  );

  // SEO text and breadcrumbs are for search engines, not for people in the app.
  if (isApp) return null;

  if (showPlasmicSeo) {
    return (
      <>
        <Fragment
          name="SeoBox"
          props={{
            seoInfo: seoInfo,
            footer: footers,
          }}
        />
        {relatedBlock}
      </>
    );
  }

  return (
    <>
      {!!seoInfo?.breadcrumbs && (
        <Breadcrumbs
          className="py-5"
          items={seoInfo.breadcrumbs.map((item, index, items) => ({ ...item, href: index + 1 < items.length ? item.href : '' }))}
        />
      )}
      {customize.showSeoBoxs && (
        <div className="flex pwa:hidden flex-col !mt-5 space-y-2">
          <Text as="h1" fontWeight="bold">
            {seoInfo?.heading}
          </Text>
          <Text as="p" fontSize="sm">
            {seoInfo?.description}
          </Text>
          <Accordion title="درباره این صفحه" className="!bg-white shadow-card !mt-5">
            <div className="text-justify text-sm [&>h2]:font-bold" dangerouslySetInnerHTML={{ __html: seoInfo?.seo_box ?? '' }} />
            {footers?.map((item: any, index: any) => (
              <Accordion key={index} title={item.title} className="mt-2">
                <MenuList className="flex !flex-row flex-wrap gap-x-14">
                  {item.items.map((menu: any) => (
                    <MenuItem key={menu.name} name={menu.name} link={menu.url} className="flex-[1_1_25rem] !justify-start">
                      <ChevronIcon dir="left" />
                    </MenuItem>
                  ))}
                </MenuList>
              </Accordion>
            ))}
          </Accordion>
        </div>
      )}
      {relatedBlock}
    </>
  );
};

export default SearchSeoBox;

